import { Calendar1, Filter, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
  useMemo,
} from "react";
import { Button } from "../Button/Button";
import { useTranslation } from "react-i18next";
import GenericFilterDropdown from "./GenericFilterDropdown";
import GenericNumberInput from "./GenericNumberInput";
import ScoreRangeFilter from "./ScoreRangeFilter";
import StatusDropdown from "./StatusDropdown";
import SearchDropdown from "../Search/SearchDropdown";
import EmployeeAttributeDropdown from "./EmployeeAttributeDropdown";
import CommonExport from "../Export/CommonExport";
import IngestionTypeDropdown from "./IngestionTypeDropdown";
import ScoreDropdown from "./ScoreDropdown";
import DateTypeDropdown from "./DateTypeDropdown";
import { cn } from "../../../utils";
import { Spinner, ToggleSwitch } from "flowbite-react";
import dayjs from "dayjs";
import {
  getDateFormatByLocale,
  safeParseDate,
} from "../../../utils/commonUtils";
import YearDropdown from "./YearDropdown";
import MonthDropdown from "./MonthDropdown";
import { Label } from "../../forms/Label/Label";
import SelectDropdown, { type TSelectOption } from "../../forms/Select/Select";
import { useToast } from "../Toast";
import WeeklyPeriodDrodown from "./WeeklyPeriodDropdown";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { TBaseDataSource, TFilterPopoverProps } from "@/types/filter";
import { useComponentCapabilities } from "@/hooks/filter-hooks/useComponentCapabilities";
import FilterChips from "./FilterChips";
import { useBasicFlags } from "@/hooks/useBasicFlags";
import { useClickOutside } from "@/hooks/filter-hooks/useClickOutside";
import { useFilterExport } from "@/hooks/filter-hooks/useFilterExport";
import { useTheme } from "../../../components/ui";
import {
  TCampaignsResultData,
  TEntityResultData,
  TLocationResultData,
  TServiceResultData,
  TEmployeeAgentListData,
  TEmployeeListBasedRankResData,
  TEmployeeSupervisorListDataSchema,
} from "@/api/common/common.types";
import type { TEmployee } from "@/api/employee-management/employee-management.types";
import { TManageTemplateObjData } from "@/api/manage-template";
import type { TAIEngineConfigEngine } from "@/api/ai-engine-config/ai-engine-config.types";
import UserPermissionRangeCount from "./UserPermissionRangeCount";
import React from "react";
import ViewHierarchyTypesList from "../Filter/ViewHierarchyTypesList";
import FrequencyFilterSelect from "./FrequencyFilterSelect";
import AiComplianceFilterSelect from "./AiComplianceFilterSelect";
import AchievementDropdown from "./AchievementDropdown";

// Accumulated options from dropdowns (includes items loaded via pagination)
// Updated by dropdowns via onOptionsChange callbacks
export type TAccumulatedOptions = {
  entity: TEntityResultData[];
  location: TLocationResultData[];
  template: TManageTemplateObjData[];
  qaSupervisor: TEmployeeSupervisorListDataSchema[];
  employee: TEmployee[];
  service: TServiceResultData[];
  campaign: TCampaignsResultData[];
  agent: TEmployeeAgentListData[];
  rankBasedAuditor: TEmployeeListBasedRankResData[];
  aiEngine: TAIEngineConfigEngine[];
};

export type TAppliedFilters = {
  hierarchyScope?: string;
  dateType?: string;
  startDate?: string;
  endDate?: string;
  status?: string | boolean;
  location?: string;
  searchType?: string;
  searchQuery?: string;
  employeeAttrKey?: string;
  employeeAttrValue?: string;
  entity?: string;
  type?: string;
  campaign?: TFilterSelection;
  template?: TFilterSelection;
  agent?: TFilterSelection;
  supervisor?: TFilterSelection;
  service?: TFilterSelection;
  employee?: TEmployeeAttributeFilter;
  rankBasedAuditorId?: string;
  score?: number | null;
  year?: string;
  month?: string;
  templateId?: string;
  aiEngine?: string;
  frequency?: string;
  aiCompliance?: string;
  periodValue?: string;
  permissionCountMin?: string;
  permissionCountMax?: string;
  userCountMin?: string;
  userCountMax?: string;
  csat?: string;
  fcr?: string;
  profanity?: string;
  aiScoreMin?: string;
  aiScoreMax?: string;
  qaScoreMin?: string;
  qaScoreMax?: string;
  achievement?: string;
  showExport?: boolean;
  targetLanguageIds?: string[];
  targetLanguageNames?: string[];
};

export type TFilterSelection = {
  id: string;
  name: string;
};

export type TEmployeeAttributeFilter = {
  key: string;
  value: string;
};

const DEFAULT_FILTER_VALUES: TAppliedFilters = {
  hierarchyScope: "",
  startDate: "",
  endDate: "",
  searchType: "",
  searchQuery: "",
  score: null,
  templateId: "",
  aiEngine: "",
  frequency: "",
  aiCompliance: "",
  location: "",
  status: "",
  entity: "",
  type: "",
  permissionCountMin: "",
  permissionCountMax: "",
  userCountMin: "",
  userCountMax: "",
  csat: "",
  fcr: "",
  profanity: "",
  aiScoreMin: "",
  aiScoreMax: "",
  qaScoreMin: "",
  qaScoreMax: "",
  achievement: "",
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const CLEAR_MAP: Record<string, Partial<TAppliedFilters>> = {
  hierarchyScope: { hierarchyScope: "" },
  entity: { entity: "" },
  campaign: { campaign: { id: "", name: "" } },
  location: { location: "" },
  agent: { agent: { id: "", name: "" } },
  supervisor: { supervisor: { id: "", name: "" } },
  rankBasedAuditor: { rankBasedAuditorId: "" },
  service: { service: { id: "", name: "" } },
  score: { score: null },
  type: { type: "" },
  template: { template: { id: "", name: "" } },
  aiEngine: { aiEngine: "" },
  frequency: { frequency: "" },
  aiCompliance: { aiCompliance: "" },
  targetLanguage: { targetLanguageIds: [], targetLanguageNames: [] },
  date_range: { startDate: "", endDate: "" },
  csat: { csat: "" },
  fcr: { fcr: "" },
  profanity: { profanity: "" },
  achievement: { achievement: "" },
};

const ACTIVE_FILTER_CAPABILITY_KEYS = [
  "hasSearchDropdown",
  "hasEmployeeAttrDropdown",
  "hasHierarchyScopeType",
  "hasDateType",
  "hasAgent",
  "hasCampaign",
  "hasService",
  "hasWeeklyPeriod",
  "hasSupervisor",
  "hasScore",
  "hasTemplate",
  "hasAiEngine",
  "hasFrequency",
  "hasAiCompliance",
  "hasIngestionType",
  "hasLocation",
  "hasStatus",
  "hasPermissionCountRange",
  "hasRankBasedAuditor",
  "hasTargetLanguage",
  "hasCSAT",
  "hasFCR",
  "hasProfanity",
  "hasAIScoreRange",
  "hasQAScoreRange",
  "hasAchievement",
] as const;

const EMPTY_SELECTION: TFilterSelection = { id: "", name: "" };

const createInitialFilterState = (
  initialFilters: TAppliedFilters | undefined,
  hasDateType: boolean | undefined,
  filterMode: "dateRange" | "yearMonth",
): TAppliedFilters => {
  const initial = initialFilters ?? {};
  const campaign = initial.campaign ?? EMPTY_SELECTION;
  const agent = initial.agent ?? EMPTY_SELECTION;
  const supervisor = initial.supervisor ?? EMPTY_SELECTION;
  const template = initial.template ?? EMPTY_SELECTION;
  const service = initial.service ?? EMPTY_SELECTION;
  const employee = initial.employee ?? {
    key: initial.employeeAttrKey ?? "",
    value: initial.employeeAttrValue ?? "",
  };
  const now = new Date();

  return {
    ...DEFAULT_FILTER_VALUES,
    ...initial,
    campaign,
    agent,
    supervisor,
    template,
    service,
    employee,
    employeeAttrKey: employee.key,
    employeeAttrValue: employee.value,
    dateType: hasDateType ? initial.dateType : "call_date",
    startDate: initial.startDate ?? "",
    endDate: initial.endDate ?? "",
    year:
      initial.year ??
      (filterMode === "yearMonth" ? String(now.getFullYear()) : ""),
    month:
      initial.month ??
      (filterMode === "yearMonth"
        ? String(now.getMonth() + 1).padStart(2, "0")
        : ""),
    searchType: initial.searchType ?? "",
    searchQuery: initial.searchQuery ?? "",
    periodValue: initial.periodValue ?? "last-week",
    rankBasedAuditorId: initial.rankBasedAuditorId ?? "",
    targetLanguageIds: initial.targetLanguageIds ?? [],
    targetLanguageNames: initial.targetLanguageNames ?? [],
  };
};

// FilterPopover component for filtering options
const FilterPopover = ({
  initialFilters,
  component,
  onApplyFilters,
  onClearFilters,
  statusData,
  // hideViewTypes,
  locationOptions,
  achievementOptions,
  tableColumns = [],
  handleSearch,
  entityOptions,
  typeOptions,
  campaignData,
  agentListData,
  qaSupervisorData,
  employeeListData,
  templateData,
  serviceData,
  rankBasedAuditorData,
  employeeAttributeDefinitions,
  aiEngineData,
  languageData,
  filterMode = "dateRange",
  setFilterMode,
  id,
  defaultFilters,
  showExport,
}: TFilterPopoverProps) => {
  // Component capabilities
  const capabilities = useComponentCapabilities(component ?? "");
  const { isSuperuser } = useBasicFlags();

  // Export functionality
  const { isExporting, handleExport } = useFilterExport();

  // Popover state
  const [filterPopover, setFilterPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useClickOutside(
    [popoverRef, buttonRef],
    () => setFilterPopover(false),
    filterPopover,
  );

  // React 18 concurrent features
  const [isApplyingFilters, startApplyTransition] = useTransition();

  // Translation and theme
  const { t } = useTranslation([
    "common",
    "dataIngestionPage",
    "evaluationListingPage",
    "roleManagementPage",
    "sttEngineConfigurationPage",
  ]);
  const { i18n } = useTranslation();
  const { toast } = useToast();
  const isRTL = i18n.language === "ar";
  const { theme } = useTheme();
  const glassClass = theme === "dark" ? "filter-bg-blur" : "";
  // Range validation state
  const [isError, setIsError] = useState(false);
  // Year options
  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year();
    return Array.from({ length: 11 }, (_, i) => String(currentYear - 5 + i));
  }, []);

  const languageOptions = useMemo<TSelectOption[]>(
    () =>
      languageData?.data?.map((language) => ({
        value: language.internal_id,
        label: language.name,
      })) ?? [],
    [languageData?.data],
  );

  // ======================UNIFIED FILTER STATE =================================

  const getInitialFilterState = useCallback(
    () =>
      createInitialFilterState(
        initialFilters,
        capabilities?.hasDateType,
        filterMode,
      ),
    [capabilities?.hasDateType, filterMode, initialFilters],
  );

  // Use ref instead of state to avoid re-renders during pagination
  // FilterChips will read from this ref when rendering chips
  const accumulatedOptionsRef = useRef<TAccumulatedOptions>({
    entity: entityOptions?.data ?? [],
    location: locationOptions?.data ?? [],
    template: templateData?.data ?? [],
    qaSupervisor: qaSupervisorData?.data ?? [],
    employee: employeeListData?.data ?? [],
    service: serviceData?.data ?? [],
    campaign: campaignData?.data ?? [],
    agent: agentListData?.data ?? [],
    rankBasedAuditor: rankBasedAuditorData?.data ?? [],
    aiEngine: aiEngineData?.data ?? [],
  });

  //  filters state
  const [filterState, setFilterState] = useState<TAppliedFilters>(
    getInitialFilterState,
  );

  const [appliedFilters, setAppliedFilters] =
    useState<TAppliedFilters>(filterState);

  const selectedLanguageOptions = useMemo(
    () =>
      languageOptions.filter((option) =>
        (filterState.targetLanguageIds ?? []).includes(option.value),
      ),
    [languageOptions, filterState.targetLanguageIds],
  );

  // Synchronize accumulated options aggressively when parent data reference changes
  useEffect(() => {
    accumulatedOptionsRef.current.entity = entityOptions?.data ?? [];
  }, [entityOptions?.data]);
  useEffect(() => {
    accumulatedOptionsRef.current.location = locationOptions?.data ?? [];
  }, [locationOptions?.data]);
  useEffect(() => {
    accumulatedOptionsRef.current.template = templateData?.data ?? [];
  }, [templateData?.data]);
  useEffect(() => {
    accumulatedOptionsRef.current.qaSupervisor = qaSupervisorData?.data ?? [];
  }, [qaSupervisorData?.data]);
  useEffect(() => {
    accumulatedOptionsRef.current.service = serviceData?.data ?? [];
  }, [serviceData?.data]);
  useEffect(() => {
    accumulatedOptionsRef.current.campaign = campaignData?.data ?? [];
  }, [campaignData?.data]);
  useEffect(() => {
    accumulatedOptionsRef.current.agent = agentListData?.data ?? [];
  }, [agentListData?.data]);
  useEffect(() => {
    accumulatedOptionsRef.current.aiEngine = aiEngineData?.data ?? [];
  }, [aiEngineData?.data]);

  // ======================HELPER FUNCTIONS =================================

  // Helper to update filter state (can be used with startTransition)
  const updateFilterState = useCallback((updates: Partial<TAppliedFilters>) => {
    setFilterState((prev) => {
      const next = { ...prev, ...updates };
      const employee = updates.employee ?? {
        key: updates.employeeAttrKey ?? next.employee?.key ?? "",
        value: updates.employeeAttrValue ?? next.employee?.value ?? "",
      };

      return {
        ...next,
        employee,
        employeeAttrKey: employee.key,
        employeeAttrValue: employee.value,
      };
    });
  }, []);

  // Helper to build callback with specific field overrides
  const buildCallbackFilters = useCallback(
    (overrideFields: Partial<TAppliedFilters> = {}) => ({
      ...appliedFilters,
      ...overrideFields,
    }),
    [appliedFilters],
  );

  const shouldShowDateRange = capabilities?.hasCustomizeDate
    ? filterState?.periodValue === "select-customize-date"
    : capabilities?.hasStartEndDate &&
      (filterMode === "dateRange" || filterMode === undefined);

  // ======================VALIDATION =================================

  // ======================EFFECTS =================================

  // Helper to merge new options into the accumulated options ref uniquely
  const updateAccumulatedOptions = useCallback(
    <K extends keyof TAccumulatedOptions>(
      key: K,
      newOptions: TAccumulatedOptions[K],
    ) => {
      if (!newOptions?.length) return;
      // Current options in ref (from initial data or previous dropdown loads)
      const existing = accumulatedOptionsRef.current[key];
      // Map by internal_id to dedupe and preserve latest item per id
      const map = new Map<string, TAccumulatedOptions[K][number]>();
      // Seed map with existing so we keep options across dropdown resets
      existing?.forEach((item) => {
        const itemWithId = item as { internal_id?: string };
        if (itemWithId?.internal_id) {
          map.set(itemWithId.internal_id, item);
        }
      });
      // Merge in new options (overwrites same id so we get latest from dropdown)
      newOptions?.forEach((item) => {
        const itemWithId = item as { internal_id?: string };
        if (itemWithId?.internal_id) {
          map.set(itemWithId.internal_id, item);
        }
      });
      // Write back merged, deduped list so FilterChips can resolve selected ids to labels
      accumulatedOptionsRef.current[key] = Array.from(
        map.values(),
      ) as TAccumulatedOptions[K];
    },
    [],
  );

  // Add default year/month to appliedFilters on mount or when filterMode changes to yearMonth
  useEffect(() => {
    if (
      filterMode === "yearMonth" &&
      (!appliedFilters.year || !appliedFilters.month)
    ) {
      const now = new Date();
      setAppliedFilters((prev) => ({
        ...prev,
        year: String(now.getFullYear()),
        month: String(now.getMonth() + 1).padStart(2, "0"),
      }));
    }
  }, [filterMode, appliedFilters.year, appliedFilters.month]);

  // ======================HANDLER FUNCTIONS =================================

  const togglePopover = () => setFilterPopover((prev) => !prev);

  // Apply filters with React 18 optimization
  const handleApplyFilters = () => {
    // ============ PHASE 1: SYNCHRONOUS VALIDATIONS (High Priority) ============

    if (capabilities?.hasDateType && !filterState.dateType) {
      toast({
        title: t("PLEASE_SELECT_DATE_TYPE"),
        variant: "destructive",
      });
      return;
    }

    if (
      capabilities?.hasStartEndDate &&
      filterMode === "dateRange" &&
      !capabilities?.hasCustomizeDate &&
      component != "push-api-clients" &&
      component != "push-api-clients-history" &&
      component != "ai-engine-configurations"
    ) {
      if (!filterState.startDate || !filterState.endDate) {
        toast({
          title: t("PLEASE_SELECT_BOTH_DATES"),
          variant: "destructive",
        });
        return;
      }
    }

    // ============ PHASE 2: IMMEDIATE UI FEEDBACK (High Priority) ============
    setFilterPopover(false);

    // ============ PHASE 3: EXPENSIVE OPERATIONS (Low Priority) ============
    startApplyTransition(() => {
      const newFilters = structuredClone(filterState);

      setAppliedFilters(newFilters);
      onApplyFilters?.(newFilters);
    });
  };

  // Clear filters with optimized updates
  const handleClearFilters = () => {
    const defaults = createInitialFilterState(
      defaultFilters ?? getInitialFilterState(),
      capabilities?.hasDateType,
      filterMode,
    );

    setFilterState(defaults);
    setAppliedFilters(defaults);

    onApplyFilters?.(defaults);
    onClearFilters?.();
    setFilterPopover(false);
  };

  // Clear individual filter chips with transition
  const handleClearFilterChips = (type: string) => {
    let filterStateUpdates: Partial<TAppliedFilters> = CLEAR_MAP[type] ?? {};
    let appliedFilterUpdates = filterStateUpdates;

    switch (type) {
      case "status":
        filterStateUpdates = {
          status: component === "push-api-clients" ? undefined : "",
        };
        appliedFilterUpdates = filterStateUpdates;
        break;
      case "period":
        filterStateUpdates = {
          periodValue: capabilities?.hasThisWeekOption
            ? "this-week"
            : "last-week",
        };
        appliedFilterUpdates = filterStateUpdates;
        break;
      case "searchQuery":
        filterStateUpdates = {
          searchType:
            component === "evaluation-list"
              ? "ai_score"
              : component === "employee-management"
                ? "employee_id"
                : "",
          searchQuery: "",
        };
        appliedFilterUpdates = filterStateUpdates;
        break;
      case "employeeAttrValue":
        filterStateUpdates = { employeeAttrKey: "", employeeAttrValue: "" };
        appliedFilterUpdates = filterStateUpdates;
        break;
      case "permissionCount":
        filterStateUpdates = {
          permissionCountMin: "",
          permissionCountMax: "",
        };
        appliedFilterUpdates = {
          permissionCountMin: undefined,
          permissionCountMax: undefined,
        };
        break;
      case "userCount":
        filterStateUpdates = { userCountMin: "", userCountMax: "" };
        appliedFilterUpdates = {
          userCountMin: undefined,
          userCountMax: undefined,
        };
        break;

      case "aiScore":
        filterStateUpdates = { aiScoreMin: "", aiScoreMax: "" };
        appliedFilterUpdates = { aiScoreMin: "", aiScoreMax: "" };
        break;
      case "qaScore":
        filterStateUpdates = { qaScoreMin: "", qaScoreMax: "" };
        appliedFilterUpdates = { qaScoreMin: "", qaScoreMax: "" };
        break;
    }

    if (Object.keys(filterStateUpdates).length === 0) return;

    updateFilterState(filterStateUpdates);
    setAppliedFilters((prev) => ({ ...prev, ...appliedFilterUpdates }));
    onApplyFilters?.(buildCallbackFilters(appliedFilterUpdates));
  };

  const activeFilterCount =
    ACTIVE_FILTER_CAPABILITY_KEYS.filter((key) => capabilities?.[key]).length +
    Number(
      Boolean(capabilities?.hasStartEndDate && filterMode === "dateRange"),
    ) +
    Number(Boolean(capabilities?.hasYearMonth && filterMode === "yearMonth")) +
    Number(Boolean(capabilities?.hasEntity && isSuperuser));

  const renderNumberInput = (
    stateKey: "csat" | "fcr" | "profanity",
    customLabel: string,
    customPlaceholder: string,
  ) => (
    <div className="w-full">
      <GenericNumberInput
        value={filterState[stateKey]}
        onChange={(value) => {
          updateFilterState({ [stateKey]: value } as Partial<TAppliedFilters>);
        }}
        customLabel={customLabel}
        customPlaceholder={customPlaceholder}
        id={`${id}-${stateKey}-input`}
      />
    </div>
  );

  const renderSimpleDropdown = <T extends { internal_id: string }>(
    dropdownType: string,
    dataSource: TBaseDataSource<T[]> | undefined,
    stateKey: keyof TAppliedFilters,
    accumKey: keyof TAccumulatedOptions,
    selectionKey?: "campaign" | "agent" | "supervisor" | "service" | "template",
    getOptionName?: (option: T) => string,
    isMulti = false,
  ) => {
    const selectionId = selectionKey
      ? (filterState[selectionKey]?.id ?? "")
      : "";
    const selectionName = selectionKey
      ? (filterState[selectionKey]?.name ?? "")
      : "";
    const multiSelectedIds = isMulti
      ? selectionKey
        ? selectionId.split(",").filter(Boolean)
        : (filterState[stateKey] ?? "").toString().split(",").filter(Boolean)
      : [];

    return (
      <div className="w-full">
        <GenericFilterDropdown<T>
          dropdownType={dropdownType}
          dataSource={dataSource}
          isMulti={isMulti}
          value={
            selectionKey
              ? isMulti
                ? multiSelectedIds
                : selectionId
              : isMulti
                ? multiSelectedIds
                : (filterState[stateKey] as string | undefined)
          }
          formatSelectedValuesLabel={
            isMulti
              ? (values) =>
                  values.length > 3
                    ? `${values.length} selected`
                    : values.map((option) => option.label).join(", ")
              : undefined
          }
          onChange={(value) => {
            const selectedIds = Array.isArray(value)
              ? value.map(String)
              : (value?.toString().split(",").filter(Boolean) ?? []);

            const optionById = new Map<string, T>(
              (accumulatedOptionsRef.current[accumKey] as T[]).map(
                (option): [string, T] => [option.internal_id, option],
              ),
            );

            if (selectionKey && isMulti) {
              const previousIds = selectionId.split(",").filter(Boolean);
              const previousNames = selectionName.split(",");
              const names = selectedIds.map((id) => {
                const option = optionById.get(id);
                const resolvedFromOption =
                  option && getOptionName ? getOptionName(option) : "";
                const fallbackLabel = dataSource?.selectedFallbackOptions?.find(
                  (fallback) => fallback.value === id,
                )?.label;
                const previousIndex = previousIds.indexOf(id);
                const previousName =
                  previousIndex >= 0
                    ? (previousNames[previousIndex]?.trim() ?? "")
                    : "";

                return (
                  resolvedFromOption || fallbackLabel || previousName || id
                );
              });

              updateFilterState({
                [selectionKey]: {
                  id: selectedIds.join(","),
                  name: names.join(","),
                },
              } as Partial<TAppliedFilters>);
              return;
            }

            const selected = selectedIds[0];
            const option = selected ? optionById.get(selected) : undefined;
            const previousSelection = selectionKey
              ? filterState[selectionKey]
              : undefined;
            const resolvedFromOption =
              option && getOptionName ? getOptionName(option) : "";
            const fallbackLabel = dataSource?.selectedFallbackOptions?.find(
              (fallback) => fallback.value === selected,
            )?.label;
            const previousName =
              previousSelection?.id === selected
                ? (previousSelection.name ?? "")
                : "";
            const name =
              resolvedFromOption ||
              fallbackLabel ||
              previousName ||
              selected ||
              "";

            updateFilterState(
              (selectionKey
                ? { [selectionKey]: { id: selected ?? "", name } }
                : {
                    [stateKey]: selectedIds.join(","),
                  }) as Partial<TAppliedFilters>,
            );
          }}
          onOptionsChange={(options) =>
            updateAccumulatedOptions(
              accumKey,
              options as TAccumulatedOptions[typeof accumKey],
            )
          }
          accumulatedOptions={accumulatedOptionsRef.current[accumKey] as T[]}
          selectedFallbackOptions={[
            ...(dataSource?.selectedFallbackOptions ?? []),
            ...(selectionKey && selectionId && selectionName
              ? isMulti
                ? selectionId
                    .split(",")
                    .filter(Boolean)
                    .map((id, index) => ({
                      value: id,
                      label: selectionName.split(",")[index]?.trim() || id,
                    }))
                : [
                    {
                      value: selectionId,
                      label: selectionName,
                    },
                  ]
              : []),
          ]}
        />
      </div>
    );
  };

  // ======================RENDER =================================

  return (
    <>
      <div className="flex items-center flex-1" data-testid="filter-popover">
        <div className="flex w-full items-center gap-3 space-between relative">
          <div className="flex gap-2 items-top">
            <Button
              variant="outline"
              size="sm"
              ref={buttonRef}
              type="button"
              className="flex items-center gap-1 text-primary border dark:text-foreground"
              onClick={togglePopover}
              data-testid="filter-popover-btn-testid"
            >
              <Filter size={16} />
              <span>{t("FILTER.TITLE")}</span>
            </Button>
            <div className="flex-1">
              {/* Filter Chips */}
              <FilterChips
                appliedFilters={appliedFilters}
                capabilities={capabilities}
                employeeAttributeDefinitions={employeeAttributeDefinitions}
                campaignData={{
                  selectedFallBacksOptions:
                    campaignData?.selectedFallbackOptions,
                  results:
                    accumulatedOptionsRef.current.campaign.length > 0
                      ? accumulatedOptionsRef.current.campaign
                      : (campaignData?.data ?? []),
                }}
                agentListData={{
                  selectedFallBacksOptions:
                    agentListData?.selectedFallbackOptions,
                  results:
                    accumulatedOptionsRef.current.agent.length > 0
                      ? accumulatedOptionsRef.current.agent
                      : (agentListData?.data ?? []),
                }}
                serviceData={{
                  selectedFallBacksOptions:
                    serviceData?.selectedFallbackOptions,
                  results:
                    accumulatedOptionsRef.current.service.length > 0
                      ? accumulatedOptionsRef.current.service
                      : (serviceData?.data ?? []),
                }}
                id={`${component}-filter-chips`}
                entityOptions={{
                  selectedFallBacksOptions:
                    entityOptions?.selectedFallbackOptions,
                  results:
                    accumulatedOptionsRef.current.entity.length > 0
                      ? accumulatedOptionsRef.current.entity
                      : (entityOptions?.data ?? []),
                }}
                templateData={{
                  selectedFallBacksOptions:
                    templateData?.selectedFallbackOptions,
                  results:
                    accumulatedOptionsRef.current.template.length > 0
                      ? accumulatedOptionsRef.current.template
                      : (templateData?.data ?? []),
                }}
                locationOptions={{
                  selectedFallBacksOptions:
                    locationOptions?.selectedFallbackOptions,
                  results:
                    accumulatedOptionsRef.current.location.length > 0
                      ? accumulatedOptionsRef.current.location
                      : (locationOptions?.data ?? []),
                }}
                qaSupervisorData={{
                  selectedFallBacksOptions:
                    qaSupervisorData?.selectedFallbackOptions,
                  results:
                    accumulatedOptionsRef.current.qaSupervisor.length > 0
                      ? accumulatedOptionsRef.current.qaSupervisor
                      : (qaSupervisorData?.data ?? []),
                }}
                rankBasedAuditorData={{
                  selectedFallBacksOptions:
                    qaSupervisorData?.selectedFallbackOptions,
                  results:
                    accumulatedOptionsRef.current.rankBasedAuditor.length > 0
                      ? accumulatedOptionsRef.current.rankBasedAuditor
                      : (rankBasedAuditorData?.data ?? []),
                }}
                tableColumns={tableColumns}
                component={component}
                statusData={{
                  results: statusData?.data ?? [],
                }}
                integrationPartner={{
                  results: typeOptions?.data ?? [],
                }}
                aiEngineData={{
                  results:
                    accumulatedOptionsRef.current.aiEngine.length > 0
                      ? accumulatedOptionsRef.current.aiEngine
                      : (aiEngineData?.data ?? []),
                }}
                languageData={{
                  results: languageData?.data ?? [],
                }}
                onClearFilter={(type) => handleClearFilterChips(type)}
              />

              {/* Loading indicator for filter application */}
              {isApplyingFilters && (
                <div className="text-sm text-muted-foreground flex items-center gap-2 mt-2">
                  <Spinner size="sm" />
                  <span>Loading...</span>
                </div>
              )}
            </div>

            {/* Filter Popover */}
            {filterPopover && (
              <div
                ref={popoverRef}
                className={cn(
                  "absolute top-full z-50 mt-2 rounded-lg border bg-background shadow-xl min-w-[400px]",
                  activeFilterCount > 3
                    ? "w-[calc(100%-10px)]"
                    : "w-fit max-w-[calc(100%-10px)]",
                  isRTL ? "right-0" : "left-0",
                  glassClass,
                )}
                data-testid="filter-popover-panel"
                data-glass={glassClass || undefined}
              >
                <div className="p-4" style={{ position: "relative" }}>
                  <Button
                    type="button"
                    variant={"ghost"}
                    size={"icon"}
                    onClick={togglePopover}
                    className={cn(
                      "absolute top-2 p-1.5",
                      isRTL ? "left-2" : "right-2",
                    )}
                    aria-label="Close filter popover"
                    data-testid="close-filter-popover-btn-testid"
                  >
                    <X size={20} />
                  </Button>

                  <div
                    className={`grid gap-4 grid-cols-[repeat(auto-fit,minmax(300px,1fr))] pt-4`}
                  >
                    {/* Search Dropdown */}
                    {capabilities?.hasSearchDropdown && (
                      <div className="w-full lg:col-span-1">
                        <Label
                          weight={"medium"}
                          aria-label="Search By"
                          htmlFor="Search By"
                          className="mb-1"
                        >
                          {t("SEARCH_BY")}
                        </Label>
                        <SearchDropdown
                          searchType={filterState.searchType ?? ""}
                          setSearchType={(value) => {
                            updateFilterState({ searchType: value });
                          }}
                          searchQuery={filterState.searchQuery ?? ""}
                          setSearchQuery={(value) => {
                            updateFilterState({ searchQuery: value });
                          }}
                          handleSearch={() => {
                            handleSearch?.();
                          }}
                          tableColumns={tableColumns}
                          defaultSearchType={
                            component === "evaluation-list"
                              ? "ai_score"
                              : component === "employee-management" ||
                                  component === "manage-user"
                                ? "employee_id"
                                : "agent"
                          }
                          id={`${component}-search-dropdown`}
                        />
                      </div>
                    )}

                    {/* Toggle between Date Range and Year/Month */}
                    {capabilities?.hasToggle && (
                      <div className="w-full lg:col-span-2">
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-medium text-foreground">
                            {t("FILTER.CALL_DATE_RANGE")}
                          </span>
                          <ToggleSwitch
                            checked={filterMode === "yearMonth"}
                            onChange={() =>
                              setFilterMode?.(
                                filterMode === "dateRange"
                                  ? "yearMonth"
                                  : "dateRange",
                              )
                            }
                            label=""
                            className="!mb-0"
                          />
                          <span className="text-sm font-medium text-foreground">
                            {t("FILTER.YEAR_MONTH")}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Date Type Dropdown */}
                    {capabilities?.hasDateType && component && (
                      <div className="w-full lg:col-span-1">
                        <DateTypeDropdown
                          selVal={filterState.dateType}
                          onDateTypeSel={(value) => {
                            updateFilterState({ dateType: value });
                          }}
                          includeCallDateOnly={
                            capabilities?.withoutCallDateMenuItems
                          }
                        />
                      </div>
                    )}

                    {capabilities?.hasHierarchyScopeType && (
                      <div className="w-full lg:col-span-1">
                        <ViewHierarchyTypesList
                          selVal={filterState.hierarchyScope}
                          onHierarchyTypeSel={(value) => {
                            updateFilterState({ hierarchyScope: value });
                          }}
                        />
                      </div>
                    )}

                    {/* Year/Month Dropdowns */}
                    {capabilities?.hasYearMonth &&
                      filterMode === "yearMonth" && (
                        <div className="flex gap-2 w-full">
                          <div className="flex-1">
                            <YearDropdown
                              year={filterState.year ?? ""}
                              onChange={(y) => {
                                updateFilterState({ year: y });
                              }}
                              yearRange={{
                                start: parseInt(yearOptions[0]),
                                end: parseInt(
                                  yearOptions[yearOptions.length - 1],
                                ),
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <MonthDropdown
                              month={filterState.month ?? ""}
                              onChange={(m) => {
                                updateFilterState({ month: m });
                              }}
                              monthNames={monthNames}
                            />
                          </div>
                        </div>
                      )}

                    {/* Campaign Dropdown */}
                    {capabilities?.hasCampaign &&
                      renderSimpleDropdown(
                        "campaign",
                        campaignData,
                        "campaign",
                        "campaign",
                        "campaign",
                        (campaign) => campaign.name,
                        campaignData?.isMulti ?? false,
                      )}

                    {/* STT Target Language Multiselect */}
                    {capabilities?.hasTargetLanguage && (
                      <div className="w-full space-y-1">
                        <Label weight="medium">
                          {t("sttEngineConfigurationPage:FORM.TARGET_LANGUAGE")}
                        </Label>
                        <SelectDropdown
                          name="stt-target-language-filter"
                          isMulti
                          options={languageOptions}
                          value={selectedLanguageOptions}
                          onChange={(selected: TSelectOption[] | null) =>
                            updateFilterState({
                              targetLanguageIds: (selected ?? []).map(
                                (option) => option.value,
                              ),
                              targetLanguageNames: (selected ?? []).map(
                                (option) => option.label,
                              ),
                            })
                          }
                          placeholder={
                            languageData?.isLoading
                              ? t("sttEngineConfigurationPage:FORM.LOADING")
                              : t(
                                  "sttEngineConfigurationPage:FORM.TARGET_LANGUAGE_PLACEHOLDER",
                                )
                          }
                          getOptionLabel={(option) => option.label}
                          getOptionValue={(option) => option.value}
                          isLoading={languageData?.isLoading}
                        />
                      </div>
                    )}

                    {/* Weekly Period Dropdown */}
                    {capabilities?.hasWeeklyPeriod && (
                      <div className="w-full">
                        <WeeklyPeriodDrodown
                          selVal={filterState.periodValue}
                          hideSelectDate={!capabilities?.hasCustomizeDate}
                          showThisWeek={capabilities?.hasThisWeekOption}
                          onPeriod={(value) => {
                            updateFilterState({
                              periodValue: value,
                              startDate:
                                value !== "select-customize-date"
                                  ? ""
                                  : filterState.startDate,
                              endDate:
                                value !== "select-customize-date"
                                  ? ""
                                  : filterState.endDate,
                            });
                          }}
                        />
                      </div>
                    )}

                    {/* Date Range Pickers */}
                    {shouldShowDateRange && (
                      <div className="w-full flex gap-2">
                        <div className="flex-1">
                          <Label
                            weight={"medium"}
                            aria-label="Start Date"
                            className="mb-1"
                          >
                            {t("FILTER.START_DATE")}
                          </Label>
                          <div
                            className={cn(
                              "react-datepicker-wrapper relative inline-block",
                              isRTL && "rtl-datepicker-icon-left",
                            )}
                          >
                            <DatePicker
                              icon={<Calendar1 />}
                              showMonthDropdown
                              showYearDropdown
                              showIcon
                              toggleCalendarOnIconClick
                              dropdownMode="select"
                              autoComplete="off"
                              selected={safeParseDate(filterState.startDate)}
                              onChange={(date: Date | null) => {
                                if (date) {
                                  const formattedDate = format(
                                    date,
                                    "yyyy-MM-dd",
                                  );
                                  updateFilterState({
                                    startDate: formattedDate,
                                  });
                                } else {
                                  updateFilterState({ startDate: "" });
                                }
                              }}
                              placeholderText={getDateFormatByLocale(
                                "single",
                                true,
                              )}
                              dateFormat={getDateFormatByLocale()}
                              className={cn(
                                "w-full py-2 border rounded dark:bg-gray-800 dark:text-white flex-1 dark:[color-scheme:dark]",
                                isRTL ? "pl-10 pr-3" : "pl-3 pr-10",
                              )}
                              id={`${id}-start-date-picker`}
                            />
                          </div>
                        </div>
                        <div className="flex-1">
                          <Label
                            weight={"medium"}
                            aria-label="End Date"
                            className="mb-1"
                          >
                            {t("FILTER.END_DATE")}
                          </Label>

                          <DatePicker
                            showMonthDropdown
                            showYearDropdown
                            icon={<Calendar1 />}
                            showIcon
                            dropdownMode="select"
                            disabled={!filterState.startDate}
                            selected={safeParseDate(filterState.endDate)}
                            toggleCalendarOnIconClick
                            autoComplete="off"
                            minDate={safeParseDate(filterState.startDate)}
                            onChange={(date: Date | null) => {
                              if (date) {
                                const formattedDate = format(
                                  date,
                                  "yyyy-MM-dd",
                                );
                                updateFilterState({
                                  endDate: formattedDate,
                                });
                              } else {
                                updateFilterState({ endDate: "" });
                              }
                            }}
                            placeholderText={getDateFormatByLocale(
                              "single",
                              true,
                            )}
                            dateFormat={getDateFormatByLocale()}
                            className={cn(
                              "w-full py-2 border rounded dark:bg-gray-800 dark:text-white flex-1 dark:[color-scheme:dark]",
                              isRTL ? "pl-10 pr-3" : "pl-3 pr-10",
                            )}
                            id={`${id}-end-date-picker`}
                          />
                        </div>
                      </div>
                    )}

                    {/* Service Dropdown */}
                    {capabilities?.hasService &&
                      renderSimpleDropdown(
                        "service",
                        serviceData,
                        "service",
                        "service",
                        "service",
                        (service) => service.name,
                        serviceData?.isMulti ?? false,
                      )}

                    {/* Agent Dropdown */}
                    {capabilities?.hasAgent &&
                      renderSimpleDropdown(
                        "agent",
                        agentListData,
                        "agent",
                        "agent",
                        "agent",
                        (agent) =>
                          `${agent.first_name ?? ""} ${agent.last_name ?? ""}`.trim(),
                        agentListData?.isMulti ?? false,
                      )}

                    {/* Entity Dropdown */}
                    {capabilities?.hasEntity &&
                      isSuperuser &&
                      renderSimpleDropdown(
                        "entity",
                        entityOptions,
                        "entity",
                        "entity",
                      )}

                    {/* Supervisor / Auditor Dropdown */}
                    {/* {capabilities?.hasSupervisor &&
                      renderSimpleDropdown(
                        "employeeSupervisor",
                        qaSupervisorData,
                        "supervisor",
                        "qaSupervisor",
                        "supervisor",
                        (supervisor) =>
                          `${supervisor.first_name} ${supervisor.last_name}`.trim(),
                        qaSupervisorData?.isMulti ?? false,
                      )} */}

                    {/* Employee  Attribute Dropdown */}
                    {capabilities?.hasEmployeeAttrDropdown && (
                      <div className="w-full lg:col-span-1">
                        <Label
                          weight={"medium"}
                          aria-label="Filter By"
                          htmlFor="Filter By"
                          className="mb-1"
                        >
                          {t("FILTER.EMPLOYEE_ATTRS")}
                        </Label>
                        <EmployeeAttributeDropdown
                          employeeAttributeDefinitions={
                            employeeAttributeDefinitions
                          }
                          searchKey={filterState.employeeAttrKey ?? ""}
                          setSearchKey={(value) => {
                            updateFilterState({ employeeAttrKey: value });
                          }}
                          searchValue={filterState.employeeAttrValue ?? ""}
                          setSearchValue={(value) => {
                            updateFilterState({ employeeAttrValue: value });
                          }}
                          handleSearch={() => {
                            handleSearch?.();
                          }}
                          defaultSearchKey=""
                          id={`${component}-employee-attributes-dropdown`}
                        />
                      </div>
                    )}

                    {/*  Auditor Rank Based Dropdown */}
                    {capabilities?.hasRankBasedAuditor &&
                      renderSimpleDropdown(
                        "rankBasedAuditor",
                        rankBasedAuditorData,
                        "rankBasedAuditorId",
                        "rankBasedAuditor",
                      )}

                    {/* Score Dropdown */}
                    {capabilities?.hasScore && (
                      <div className="w-full">
                        <ScoreDropdown
                          selVal={filterState.score ?? undefined}
                          onScoreSel={(value) => {
                            updateFilterState({ score: value ?? null });
                          }}
                        />
                      </div>
                    )}

                    {/* AI Engine Dropdown */}
                    {capabilities?.hasAiEngine &&
                      renderSimpleDropdown(
                        "aiEngine",
                        aiEngineData,
                        "aiEngine",
                        "aiEngine",
                      )}

                    {/* Frequency Dropdown */}
                    {capabilities?.hasFrequency && (
                      <FrequencyFilterSelect
                        value={filterState.frequency}
                        onChange={(value) => {
                          updateFilterState({ frequency: value });
                        }}
                      />
                    )}

                    {/* AI Compliance Dropdown */}
                    {capabilities?.hasAiCompliance && (
                      <AiComplianceFilterSelect
                        value={filterState.aiCompliance}
                        onChange={(value) => {
                          updateFilterState({ aiCompliance: value });
                        }}
                      />
                    )}

                    {/* Template Dropdown */}
                    {/* Template Dropdown */}
                    {capabilities?.hasTemplate &&
                      renderSimpleDropdown(
                        "template",
                        templateData,
                        "templateId",
                        "template",
                        "template",
                        (template) => template.name,
                      )}
                    {/* Ingestion Type Dropdown */}
                    {capabilities?.hasIngestionType && (
                      <div className="w-full">
                        <IngestionTypeDropdown
                          isLoading={typeOptions?.isLoading}
                          selVal={filterState.type}
                          onTypeSel={(value) => {
                            updateFilterState({ type: value?.toString() });
                          }}
                          typeOptions={typeOptions?.data}
                        />
                      </div>
                    )}

                    {/* Additional Filters */}
                    <>
                      {/* Location Dropdown */}
                      {capabilities?.hasLocation &&
                        renderSimpleDropdown(
                          "location",
                          locationOptions,
                          "location",
                          "location",
                        )}

                      {/* Status Dropdown */}
                      {capabilities?.hasStatus && (
                        <div className="w-full">
                          <StatusDropdown
                            isLoading={statusData?.isLoading}
                            selVal={filterState.status}
                            onStatusSel={(value) => {
                              updateFilterState({
                                status: value?.toString(),
                              });
                            }}
                            component={component}
                            statusData={statusData?.data}
                          />
                        </div>
                      )}
                    </>

                    {/* Permission and User Count Ranges */}
                    {capabilities?.hasPermissionCountRange && (
                      <UserPermissionRangeCount
                        filterState={filterState}
                        updateFilterState={updateFilterState}
                        id={id}
                        isError={isError}
                        onerror={(value) => setIsError(value)}
                      />
                    )}

                    {/* Filter for Agent performance report */}

                    {/* CSAT Score Input */}
                    {capabilities?.hasCSAT &&
                      renderNumberInput(
                        "csat",
                        "Avg CSAT Score",
                        "Enter CSAT Score",
                      )}

                    {capabilities?.hasAIScoreRange && (
                      <ScoreRangeFilter
                        id={`${id}-ai-score-range`}
                        label="Avg AI Score Range"
                        min={filterState.aiScoreMin ?? ""}
                        max={filterState.aiScoreMax ?? ""}
                        onMinChange={(aiScoreMin) =>
                          updateFilterState({ aiScoreMin })
                        }
                        onMaxChange={(aiScoreMax) =>
                          updateFilterState({ aiScoreMax })
                        }
                      />
                    )}

                    {capabilities?.hasQAScoreRange && (
                      <ScoreRangeFilter
                        id={`${id}-qa-score-range`}
                        label="Avg QA Score Range"
                        min={filterState.qaScoreMin ?? ""}
                        max={filterState.qaScoreMax ?? ""}
                        onMinChange={(qaScoreMin) =>
                          updateFilterState({ qaScoreMin })
                        }
                        onMaxChange={(qaScoreMax) =>
                          updateFilterState({ qaScoreMax })
                        }
                      />
                    )}

                    {/* FCR Input */}
                    {capabilities?.hasFCR &&
                      renderNumberInput("fcr", "Avg FCR", "Enter FCR")}

                    {/* Profanity Input */}
                    {capabilities?.hasProfanity &&
                      renderNumberInput(
                        "profanity",
                        "Avg Profanity",
                        "Enter Profanity",
                      )}

                    {/* Achievement Dropdown */}
                    {capabilities?.hasAchievement && (
                      <div className="w-full">
                        <AchievementDropdown
                          isLoading={achievementOptions?.isLoading}
                          selVal={filterState.achievement}
                          onAchievementSel={(value) => {
                            updateFilterState({ achievement: value });
                          }}
                          achievementData={achievementOptions?.data}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div
                  className={cn(
                    "my-4 flex gap-2 px-3",
                    isRTL ? "justify-start" : "justify-end",
                  )}
                >
                  <Button
                    variant={"outline"}
                    size={"sm"}
                    type="button"
                    onClick={handleClearFilters}
                    className="button-min-width"
                    data-testid="filter-popover-clear-button"
                  >
                    {t("CLEAR")}
                  </Button>
                  <Button
                    variant={"default"}
                    size={"sm"}
                    type="button"
                    disabled={isError || isApplyingFilters}
                    onClick={handleApplyFilters}
                    className="button-min-width"
                    data-testid="filter-popover-apply-button"
                  >
                    {isApplyingFilters ? <Spinner size="sm" /> : t("APPLY")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Export Button */}
          {component === "manage-user" && showExport && (
            <div className="flex-1 flex justify-end">
              <CommonExport
                isCSV={true}
                isXLS={true}
                isXLSX={true}
                isLoading={isExporting}
                onFormatSelect={(format) => {
                  void handleExport(format);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default React.memo(FilterPopover);
